'use client'

import { useEffect, useRef, useState, type ComponentProps, type ReactNode } from 'react'
import dynamic from 'next/dynamic'
import BestSidebar from './BestSidebar'
import { useScrollFold } from '@/hooks/useScrollFold'

const LazyAnimatePresence = dynamic(() => import('@/lib/motion/LazyAnimatePresence'), {
  ssr: false,
})

type ScrollFoldSidebarProps = {
  sidebar: ComponentProps<typeof BestSidebar>
  children: ReactNode
}

// Client island: owns the ranking-block measurement + scroll-fold state, renders the
// fold-in sidebar (fixed-positioned, so DOM location is irrelevant), and wraps the
// ranking content (passed as server-rendered children) with the measured ref.
export default function ScrollFoldSidebar({ sidebar, children }: ScrollFoldSidebarProps) {
  const rankingRef = useRef<HTMLDivElement>(null)
  const [threshold, setThreshold] = useState(Infinity)

  useEffect(() => {
    const element = rankingRef.current
    if (!element) return

    const updateThreshold = () => {
      const { bottom } = element.getBoundingClientRect()
      setThreshold(bottom + window.scrollY - window.innerHeight / 2)
    }

    updateThreshold()

    const resizeObserver = new ResizeObserver(updateThreshold)
    resizeObserver.observe(element)
    window.addEventListener('resize', updateThreshold)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateThreshold)
    }
  }, [])

  const fold = useScrollFold({ threshold })

  return (
    <>
      <LazyAnimatePresence>{fold && <BestSidebar {...sidebar} />}</LazyAnimatePresence>
      <div
        ref={rankingRef}
        className="mx-auto flex w-full max-w-content flex-col gap-8 pl-3 pr-3 md:pl-5 md:pr-20 lg:pr-5"
      >
        {children}
      </div>
    </>
  )
}
